import { ChevronDown, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils/utils';

export interface DiscoveryCardProps {
    isExpanded: boolean;
    onToggle: () => void;
    children: React.ReactNode;
    /** Standard title - used if header is not provided */
    title?: string;
    /** Standard icon - used if header is not provided */
    icon?: React.ElementType;
    /** Custom header content - replaces standard icon + title */
    header?: React.ReactNode;
    /** Optional class for the outer wrapper */
    className?: string;
    /** Optional class for the header container */
    headerClassName?: string;
    /** Optional class for the expanded content container */
    contentClassName?: string;
    /** Optional shadow/glow effect */
    hasGlow?: boolean;
    /** Whether the card can be expanded */
    canExpand?: boolean;
    /** Optional image URL to display at the top of expanded content */
    thumbnailUrl?: string;
    /** Optional border color for left border */
    borderColor?: string;
    /** Optional identifying number for markers/allusions */
    markerNumber?: number;
    /** Disable backdrop-blur for performance (useful when many cards render at once) */
    disableBlur?: boolean;
    /** Allow extra props like data attributes */
    [key: string]: any;
}

/**
 * A standardized "Discovery Card" with a "spine and dot" pattern.
 * Used for cultural, archaeological, and historical context snippets.
 */
export function DiscoveryCard({
    isExpanded,
    onToggle,
    children,
    title,
    icon: Icon,
    header,
    className,
    headerClassName,
    contentClassName,
    canExpand = true,
    thumbnailUrl,
    borderColor,
    markerNumber,
    disableBlur,
    ...props
}: DiscoveryCardProps) {
    return (
        <div
            className={cn("relative pl-6 group/card", className)}
            {...props}
        >
            {/* The "Spine" */}
            <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-primary/10 group-hover/card:bg-primary/30 transition-colors" />

            {/* Discovery Dot / Marker Number */}
            {markerNumber ? (
                <div
                    data-card-anchor={markerNumber}
                    className={cn(
                        "absolute left-[-10px] top-6 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold z-10 transition-all shadow-md ring-2 ring-background",
                        isExpanded ? "bg-primary text-primary-foreground" : "bg-primary/40 text-primary-foreground/70"
                    )}
                >
                    {markerNumber}
                </div>
            ) : (
                <div
                    data-card-anchor="true"
                    className={cn(
                        "absolute left-[-4px] top-6 w-2.5 h-2.5 rounded-full border-2 border-background transition-colors",
                        isExpanded ? 'bg-primary' : 'bg-primary/40'
                    )}
                />
            )}

            <div
                className={cn(
                    "rounded-xl border border-border/40 overflow-hidden transition-all duration-300",
                    isExpanded
                        ? 'bg-card/30 shadow-xl ring-1 ring-primary/10 shadow-primary/5'
                        : 'bg-card/30 hover:bg-card/50 hover:shadow-md hover:border-border/60',
                    borderColor && 'border-l-4'
                )}
                style={{
                    ...(!disableBlur && { backdropFilter: 'blur(16px)' }),
                    ...(borderColor && { borderLeftColor: borderColor })
                }}
            >
                {/* Subtle Background Watermark (focused on the right) */}
                {thumbnailUrl && (
                    <motion.div
                        initial={false}
                        animate={{
                            opacity: isExpanded ? 0.25 : 0.05,
                            scale: isExpanded ? 1.05 : 1
                        }}
                        className="absolute inset-0 pointer-events-none overflow-hidden select-none z-0"
                    >
                        <div
                            className="absolute inset-0"
                            style={{
                                backgroundImage: `url(${thumbnailUrl})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'right center',
                                maskImage: 'linear-gradient(to left, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)',
                                WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)'
                            }}
                        />
                    </motion.div>
                )}

                {/* Header Area - Clickable if canExpand */}
                <div
                    onClick={canExpand ? onToggle : undefined}
                    className={cn(
                        "p-5 flex items-center justify-between gap-4",
                        canExpand ? "cursor-pointer" : "cursor-default",
                        headerClassName
                    )}
                >
                    <div className="flex-1 min-w-0">
                        {header ? header : (
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-3">
                                    {Icon && <Icon className="h-5 w-5 text-primary/70 shrink-0" />}
                                    <h4 className="text-xl font-serif font-medium tracking-tight text-foreground/90 leading-tight truncate">
                                        {title}
                                    </h4>
                                </div>
                                {canExpand && !isExpanded && (
                                    <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.15em] text-primary/40 group-hover/card:text-primary/70 transition-colors">
                                        <Sparkles className="h-2.5 w-2.5" />
                                        <span>Discover Significance</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Expansion Arrow / Placeholder to prevent jumping */}
                    <div className="w-7 p-1 shrink-0 flex items-center justify-center">
                        {canExpand && (
                            <motion.div
                                animate={{ rotate: isExpanded ? 180 : 0 }}
                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                                className="text-muted-foreground/30"
                            >
                                <ChevronDown className="h-5 w-5" />
                            </motion.div>
                        )}
                    </div>
                </div>

                {/* Expanded Content */}
                <AnimatePresence>
                    {isExpanded && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                        >
                            <div className={cn("px-5 pb-6 pt-0 border-t border-border/10", contentClassName)}>
                                {children}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}
